import { skipToken, useInfiniteQuery } from '@tanstack/react-query';
import { getV1CompaniesCompanyIdTreadAgentConversationsConversationIdMessages } from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useMemo } from 'react';

import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import { TreadAgentMessage } from '~hooks/useTreadAgent/models';
import { TREAD_AGENT_QUERY_KEY } from '~queries/treadAgentQueryKeys';
import connection from '~services/connectionModule';
import { extractPagination } from '~services/pagination';
import { Nullable } from '~types/Nullable';

// One transcript page; exported so TreadAgentStore can fold a message into this cache shape.
export type TreadAgentMessagesPage = {
  messages: TreadAgentMessage[];
  nextCursor: string | undefined;
};

/**
 * A conversation's persisted messages, cursor-paginated newest-first. The first page is the
 * latest slice; `fetchOlder` follows the `Link` "next" cursor to page backwards through history
 * as the user scrolls up. Rooted under TREAD_AGENT_QUERY_KEY so a completed turn's prefix
 * invalidation refetches the transcript.
 */
export default function useTreadAgentMessagesInfiniteQuery(
  companyId: Nullable<string>,
  conversationId: Nullable<string>,
) {
  const query = useInfiniteQuery({
    queryKey: [TREAD_AGENT_QUERY_KEY, 'messages', companyId, conversationId],
    initialPageParam: undefined as string | undefined,
    queryFn:
      companyId && conversationId
        ? async ({ pageParam, signal }) => {
            try {
              const response =
                await getV1CompaniesCompanyIdTreadAgentConversationsConversationIdMessages(
                  {
                    path: { 'company-id': companyId, 'conversation-id': conversationId },
                    query: {
                      'page[limit]': DEFAULT_API_PAGE_SIZE,
                      ...(pageParam ? { 'page[after]': pageParam } : {}),
                    },
                    signal,
                  },
                );

              return {
                messages: response.data.data.map(TreadAgentMessage.parse),
                nextCursor: extractPagination(response).after,
              };
            } catch (error) {
              // See useTreadAgentConversationQuery: a cross-scope / missing conversation 404s;
              // treat it as an empty transcript instead of surfacing an error toast.
              if (isAxiosError(error) && error.response?.status === 404) {
                return { messages: [], nextCursor: undefined };
              }

              connection.handleRequestError(
                error,
                t('error_messages.tread_agent.failed_to_fetch_chat'),
              );
              throw error;
            }
          }
        : skipToken,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  // Pages come back newest-first (both across pages and within each page), so flattening then
  // reversing yields the oldest-to-newest order the transcript renders in.
  const messages = useMemo(
    () => (query.data?.pages ?? []).flatMap((page) => page.messages).reverse(),
    [query.data?.pages],
  );

  return {
    fetchOlder: query.fetchNextPage,
    hasOlder: query.hasNextPage,
    isLoading: query.isLoading,
    isLoadingOlder: query.isFetchingNextPage,
    messages,
  };
}
